import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Scale } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * One-time modal shown right after login when the user hasn't accepted the
 * CURRENT terms + privacy versions. Loads latest versions from /api/legal/,
 * matches against /api/legal/accepted/me, and records acceptance via /accept.
 * Silent no-op while auth is still resolving or user is not logged in.
 */
export function LegalConsentModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [checkedT, setCheckedT] = useState(false);
  const [checkedP, setCheckedP] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !user.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [tRes, pRes, meRes] = await Promise.all([
          axios.get(`${API}/api/legal/terms`),
          axios.get(`${API}/api/legal/privacy`),
          api.get("/legal/accepted/me"),
        ]);
        if (cancelled) return;
        const t = tRes.data;
        const p = pRes.data;
        const accepted = meRes.data?.acceptances || [];
        const hasCurrent = (docId, version) =>
          accepted.some((a) => a.doc_id === docId && a.version === version);
        const needsTerms = !hasCurrent("terms", t.version);
        const needsPrivacy = !hasCurrent("privacy", p.version);
        setTerms(t);
        setPrivacy(p);
        if (needsTerms || needsPrivacy) {
          setOpen(true);
        }
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/LegalConsentModal.jsx: swallowed', err); }
    })();
    return () => { cancelled = true; };
    // Only re-check consent when the user identity changes; the parent
    // `user` reference is stable across renders for a given session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const accept = async () => {
    if (!checkedT || !checkedP) return;
    setBusy(true);
    try {
      await Promise.all([
        api.post("/legal/accept", { doc_id: "terms", version: terms.version }),
        api.post("/legal/accept", { doc_id: "privacy", version: privacy.version }),
      ]);
      toast.success("Thanks — we've recorded your acceptance.");
      setOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not save your acceptance");
    } finally {
      setBusy(false);
    }
  };

  if (!terms || !privacy) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* modal is required — cannot dismiss */ }}>
      <DialogContent className="max-w-2xl" data-testid="legal-consent-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-orange-500" />
            <DialogTitle>Before you continue</DialogTitle>
          </div>
          <DialogDescription>
            Please review and accept the latest Terms of Service and Privacy Policy so we can keep serving your community.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-64 border rounded-md p-3 text-sm text-zinc-700 bg-zinc-50">
          <div className="font-semibold mb-2">{terms.title} · v{terms.version}</div>
          {terms.sections?.slice(0, 3).map((s, i) => (
            <div key={i} className="mb-3">
              <div className="font-medium">{s.title}</div>
              <div className="text-xs leading-relaxed">{s.body}</div>
            </div>
          ))}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-orange-600 underline text-xs" data-testid="consent-terms-link">Read full Terms of Service →</a>

          <div className="font-semibold mt-6 mb-2">{privacy.title} · v{privacy.version}</div>
          {privacy.sections?.slice(0, 3).map((s, i) => (
            <div key={i} className="mb-3">
              <div className="font-medium">{s.title}</div>
              <div className="text-xs leading-relaxed">{s.body}</div>
            </div>
          ))}
          <a href="/privacy" target="_blank" rel="noreferrer" className="text-orange-600 underline text-xs" data-testid="consent-privacy-link">Read full Privacy Policy →</a>
        </ScrollArea>

        <div className="space-y-2 mt-2">
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={checkedT}
              onCheckedChange={(v) => setCheckedT(!!v)}
              data-testid="consent-check-terms"
            />
            <span>I have read and accept the <a href="/terms" target="_blank" rel="noreferrer" className="text-orange-600 underline">Terms of Service (v{terms.version})</a>.</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={checkedP}
              onCheckedChange={(v) => setCheckedP(!!v)}
              data-testid="consent-check-privacy"
            />
            <span>I have read and accept the <a href="/privacy" target="_blank" rel="noreferrer" className="text-orange-600 underline">Privacy Policy (v{privacy.version})</a>.</span>
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={accept}
            disabled={!checkedT || !checkedP || busy}
            className="bg-orange-500 hover:bg-orange-600 rounded-full"
            data-testid="consent-accept"
          >
            {busy ? "Saving…" : "Accept and continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
